import React, { useState } from 'react';
import { X, QrCode, Copy, Check, Download, ExternalLink, Share2 } from 'lucide-react';

interface BioQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
  avatarUrl?: string;
  accentColor?: string;
}

export const BioQRCodeModal: React.FC<BioQRCodeModalProps> = ({
  isOpen,
  onClose,
  title,
  url,
  avatarUrl,
  accentColor = '#0FFF55',
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&color=0d0e11&bgcolor=ffffff&data=${encodeURIComponent(url)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = qrImageUrl;
    link.download = `qrcode-${title.toLowerCase().replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in select-none">
      <div className="w-full max-w-sm rounded-3xl bg-[#17181c] border border-white/10 p-6 flex flex-col items-center text-center gap-5 shadow-2xl relative overflow-hidden">
        {/* Glow Ambient */}
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ backgroundColor: accentColor }}
        />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center gap-2 pt-2">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={title}
              className="w-16 h-16 rounded-full object-cover border-2 p-0.5 shadow-lg"
              style={{ borderColor: accentColor }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-black font-extrabold"
              style={{ backgroundColor: accentColor }}
            >
              <QrCode className="w-6 h-6" />
            </div>
          )}
          <h3 className="text-lg font-bold text-white leading-tight">{title}</h3>
          <p className="text-xs text-gray-400">Escaneie o QR Code para acessar no celular</p>
        </div>

        {/* QR Code Container */}
        <div className="p-4 bg-white rounded-2xl shadow-xl relative group">
          <img
            src={qrImageUrl}
            alt={`QR Code para ${title}`}
            className="w-52 h-52 object-contain"
          />
        </div>

        {/* URL Box */}
        <div className="w-full bg-[#0d0e11] border border-white/10 rounded-xl p-2.5 flex items-center justify-between gap-2 text-xs">
          <span className="text-gray-300 font-mono truncate text-[11px] pl-1">{url}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-all active:scale-95 flex-shrink-0"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-[#0FFF55]" />
                <span className="text-[#0FFF55]">Copiado!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 text-gray-400" />
                <span>Copiar</span>
              </>
            )}
          </button>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 w-full pt-1">
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs border border-white/10 transition-all active:scale-95"
          >
            <Download className="w-4 h-4 text-gray-300" />
            <span>Baixar Imagem</span>
          </button>

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-black font-extrabold text-xs shadow-lg transition-all active:scale-95"
            style={{ backgroundColor: accentColor }}
          >
            <ExternalLink className="w-4 h-4" />
            <span>Abrir Link</span>
          </a>
        </div>
      </div>
    </div>
  );
};
